import { useIsModalOpen } from '@codegouvfr/react-dsfr/Modal/useIsModalOpen';
import { SIZE_10MO_OCTETS, useTextes } from '@common/config';
import { useSessionContext } from '@common/hooks';
import { EtablissementDto, useDepositOrganismeEtablissementFichierMutation } from '@common/services';
import { EtablissementFichierType } from '@common/services/etablissement-fichier';
import {
  DsfrModal,
  FileUploadField,
  Form,
  FormMethodsRef,
  JPG_PNG_PDF_EXTENSIONS,
  JPG_PNG_PDF_FILE_TYPES,
  SelectField,
} from '@common/ui';
import { appToast } from '@common/ui/toast';
import { fetchLocalFile } from '@common/utils/file';
import { useRef, useState } from 'react';
import { z } from 'zod';
import { depotAttestationSchema } from '../schemas/depotAttestation.schema';


export const DepotAttestationModal = ({ dsfrModal, etablissements }: DepotAttestationModalProps) => {
  const t = useTextes('ParcoursOfa', 'Campagne');
  const [depositEtablissementFichier, { isLoading: isDeposing }] = useDepositOrganismeEtablissementFichierMutation();
  const buttonSubmitRef = useRef<HTMLButtonElement | null>(null);
  const formRef = useRef<FormMethodsRef | null>(null);

  useIsModalOpen(dsfrModal, {
    onConceal: () => formRef.current?.reset(),
  });

  const { organismeId } = useSessionContext();

  const declarantPrincipal = etablissements[0]; // Prévoir changement ici, ce n'est pas l'établissement principal mais le premier déclarant que l'on trouve, "principal" signifie celui qui déclare une consolidée.
  const [declarantId, setDeclarantId] = useState(declarantPrincipal.id.toString());

  const declarantsOptions = etablissements.map(e => ({
    label: e.denominationUsuelle,
    value: e.id.toString(),
  }));

  const values = {
    etablissementDeclarant: declarantId,
    fichierAttestation: null,
  };

  const handleClose = () => {
    dsfrModal.close();
    formRef.current?.reset();
  };

  const handleSubmit = async (result: FormResult) => {
    const file: File | null = await fetchLocalFile(result.fichierAttestation.url, result.fichierAttestation.name);
    if (!file) return appToast.error('Impossible de récupérer le fichier déposé.');

    const formData = new FormData();
    formData.set('fichier', file);

    await depositEtablissementFichier({
      id: organismeId,
      etablissementId: Number(declarantId),
      fichierType: EtablissementFichierType.ATTESTATION,
      formData,
    }).unwrap();

    handleClose();
  };

  return (
    <dsfrModal.Component
      title={'Déposer mon attestation comptable'}
      className={'text-left'}
      size="large"
      topAnchor={false}
      buttons={[
        {
          children: 'ANNULER',
          className: 'text-red-400!',
          doClosesModal: true,
          priority: 'tertiary',
          onClick: handleClose,
        },
        {
          children: 'ENVOYER',
          doClosesModal: false,
          priority: 'primary',
          onClick: () => buttonSubmitRef.current?.click(),
          disabled: isDeposing,
        },
      ]}>

      <Form<FormResult>
        schema={depotAttestationSchema}
        onSubmit={handleSubmit}
        buttonSubmitRef={buttonSubmitRef}
        methodsRef={formRef}
        values={values}
      >
        <SelectField name={'etablissementDeclarant'}
                     options={declarantsOptions}
                     title={'Etablissement déclarant'}
                     onSelect={(id) => setDeclarantId(id)}
                     disabled={(declarantsOptions.length === 1)}
        />
        <FileUploadField name={'fichierAttestation'}
                         extensions={JPG_PNG_PDF_EXTENSIONS}
                         fileTypes={JPG_PNG_PDF_FILE_TYPES}
                         maxSize={SIZE_10MO_OCTETS}
                         requiredErrorMsg={t['DEPOT_FICHIER_TEXTE1']}
                         typesSelection={['Attestation']}
        />
      </Form>

    </dsfrModal.Component>
  );
};

interface DepotAttestationModalProps {
  dsfrModal: DsfrModal;
  etablissements: EtablissementDto[];
}

type FormResult = z.infer<typeof depotAttestationSchema>